import {
  createFileRoute,
  stripSearchParams,
  type SearchMiddleware,
  type SearchSchemaInput,
} from "@tanstack/react-router";
import { ArtikelSeite } from "@/components/ArtikelSeite";
import { herkunftAus } from "@/lib/herkunft";
import { ausSearch, LEERE_EINZELWERTE, type SucheFilter } from "@/lib/suche-filter";

/*
 * `/artikel/$id`. Die zweite Route mit einem Pfad-Parameter.
 *
 * Diese Datei liest den Parameter und reicht ihn als Prop weiter — die
 * Komponente bleibt dadurch ohne Router testbar. Dieselbe Trennung wie bei
 * `lieferant.$name.tsx`, und aus demselben Grund: ein `useParams()` in der
 * Komponente zöge den Router in jeden ihrer Tests.
 *
 * TanStack Router liefert den Wert bereits DEKODIERT. Die ID ist im Index aus
 * `mandant_lagerort_artikel_charge` zusammengesetzt, und die Charge ist ein
 * ERP-Freitextfeld — aus `/artikel/1_100_4711_A%2FB` wird hier `1_100_4711_A/B`,
 * und genau dieser Wert geht als EIN Pfad-Parameter weiter, nie als Stück
 * eines selbstgebauten Pfads (siehe `api/artikel.ts`).
 *
 * Wer von hier aus verlinkt, gibt `params` als Objekt an und lässt den Router
 * kodieren (`<Link to="/artikel/$id" params={{ id }} />`).
 *
 * ═══ Diese Route steht bewusst NICHT in der Navigation ════════════════════
 *
 * `Navigation.tsx` schließt Parameter-Routen aus (`if (pfad.includes("$"))
 * return null`), und das ist richtig: ein Nav-Eintrag „/artikel/$id" führte
 * auf einen Platzhalter statt auf einen Artikel. Die Go-Seite sagt dasselbe —
 * die Detailseite trägt keinen `auth.NavItems`-Eintrag, weil sie kein Nav-Ziel
 * ist, sondern die GEMEINSAME Seite aller Sichten
 * (`internal/auth/navigation.go`, `internal/api/artikel.go`).
 *
 * ═══ Was das `validateSearch` hier tut — und was NICHT ═══════════════════
 *
 * Es liest den SUCHFILTER, mit dem der Lesende hergekommen ist, und reicht
 * ihn weiter. Diese Seite filtert damit nichts; sie zeigt einen Artikel, und
 * der Filter interessiert sie an genau einer Stelle: dem Rückweg (Posten 63).
 * Wer aus der Trefferliste springt, soll seine Suche wiederfinden, statt sie
 * neu zusammenzuklicken.
 *
 * Dieselbe Funktion wie in `/suche` und `/verkaufssuche` — drei Auslegungen
 * derselben Parameter wären drei Wahrheiten. Ein Filter, der von hier
 * zurückgeht, ist deshalb zeichengleich mit dem, der herkam.
 *
 * `von=verkaufssuche` wirkt zusätzlich als Herkunftssignal für die
 * Tabellenvariante. Es bleibt in der Seitenadresse `von`, damit Rückweg und
 * Suchfilter eindeutig bleiben; `ArtikelSeite` übersetzt diese Herkunft beim
 * API-Aufruf in `view=verkauf`. Die Rollen-Tiefe entscheidet weiterhin der
 * Server und lässt sich dadurch nicht erweitern.
 *
 * ═══ Warum die Middleware hier MITMUSS ═══════════════════════════════════
 *
 * Mit dem `validateSearch` fällt diese Route unter denselben Mechanismus wie
 * die beiden Suchsichten: der Router baut die Adresse beim Mount neu und
 * setzt die weggelassenen Felder wieder ein. Ohne die Middleware stünde ab
 * sofort `?q=&bw=&sort=&page=` auch hier — an einer Adresse, die man am
 * Telefon vorliest. Gepinnt in `suche-filter.test.ts` und in
 * `suche-artikel-lieferant.spec.ts`.
 */
/**
 * Die HERKUNFT — der zweite, vom Filter unabhängige Teil dieser Adresse.
 *
 * ═══ Warum sie NEBEN dem Filter steht und nicht darin ════════════════════
 *
 * Weil `SucheFilter` das Modell ist, das der SERVER kennt, und weil eine
 * vierte Bedeutung in `OHNE_LEERE_EINZELWERTE` genau in die
 * Adressen-Wiederaufbau-Falle von Posten 62 liefe. Die vollständige
 * Begründung — vier Punkte, jeder einzeln — steht im Kopf von
 * `lib/herkunft.ts`; sie gehört zum Modell und nicht zu dieser Route.
 *
 * Der zweite Eintrag in `middlewares` ist die Entsprechung zu
 * `OHNE_LEERE_HERKUNFT` an `/lieferant/$name`, und er ist aus demselben Grund
 * nötig: mit einem `validateSearch` baut der Router die Adresse beim Mount
 * neu und setzt weggelassene Felder wieder ein. Ohne ihn stünde ab sofort
 * `?von=` an JEDER Artikel-Adresse.
 *
 * Er kann NICHT `OHNE_LEERE_EINZELWERTE` sein: das ist eine Middleware über
 * `SucheFilter` und kennt dessen 19 Felder in drei Wertsorten. `von` ist
 * keines davon.
 */
export interface ArtikelHerkunftSuche {
  /**
   * Die Marke der Herkunft — `""` heisst „unbekannt".
   *
   * Ein STRING und keine geprüfte Form: was hier steht, geht durch
   * `herkunftAus`, und alles Unbekannte ergibt dort `null` und damit keinen
   * Rückweg. Eine Prüfung an dieser Stelle wäre eine zweite Wahrheit neben
   * dem Leser — und sie müsste eine erfundene Marke zu einem FEHLER machen,
   * obwohl sie bloss eine Adresse ist, die nichts aussagt.
   */
  von: string;
}

/**
 * Die Middleware dieser Route — Filter UND Herkunft in EINER.
 *
 * ═══ Warum hier nicht `OHNE_LEERE_EINZELWERTE` steht ═════════════════════
 *
 * Nicht aus Geschmack, sondern weil es nicht typprüft: jene Konstante ist
 * `SearchMiddleware<SucheFilter>`, und der Suchtyp dieser Route ist um `von`
 * breiter. TanStack Router typisiert `next` kontravariant — eine Middleware
 * über dem schmaleren Typ dürfte den breiteren gar nicht durchreichen.
 *
 * Der Ausweg ist NICHT, `suche-filter.ts` zu lockern (das wäre eine
 * Aufweichung für alle drei Routen, die sie benutzen) und auch kein `as` —
 * eine Zusicherung, die genau die Prüfung abschaltet, die hier richtig
 * angeschlagen hat.
 *
 * Stattdessen wird `LEERE_EINZELWERTE` GESPREIZT. Das ist derselbe Wert, aus
 * dem `OHNE_LEERE_EINZELWERTE` gebaut ist, und damit bleibt die Feldliste an
 * genau EINER Stelle: kommt dort ein fünfter Einzelwert dazu, wandert er hier
 * ohne Zutun mit. Die Konstante ist `Record<EinzelParameter, "">` und
 * erzwingt Vollständigkeit — eine vergessene Ergänzung wäre dort ein
 * Compilerfehler, nicht hier ein stiller Ausfall.
 *
 * ═══ Was sie NICHT anfasst — Posten 62 ═══════════════════════════════════
 *
 * Die LISTEN-Marker. `stripSearchParams` vergleicht mit `deepEqual` und wirft
 * nur weg, was GLEICH ist:
 *
 *     deepEqual("",   "")  →  true    (`q=` und `von=` fallen weg)
 *     deepEqual([""], "")  →  false   (`loc=` bleibt stehen)
 *
 * `loc=` heisst „alle Standorte" und ist bedeutungstragend. Beide Richtungen
 * sind in `suche-filter.test.ts` gepinnt; diese Zeile ändert daran nichts,
 * weil sie denselben Vergleich mit denselben Vorgaben fährt.
 */
const OHNE_LEERE_EINZELWERTE_UND_HERKUNFT: SearchMiddleware<
  SucheFilter & ArtikelHerkunftSuche
> = stripSearchParams({ ...LEERE_EINZELWERTE, von: "" });

export const Route = createFileRoute("/artikel/$id")({
  component: ArtikelRoute,
  validateSearch: (
    roh: Record<string, unknown> & SearchSchemaInput,
  ): SucheFilter & ArtikelHerkunftSuche => ({
    ...ausSearch(roh),
    // Mehrfach angegeben gewinnt das ERSTE Vorkommen — dieselbe Regel wie
    // `alsSchalter` in `lib/suche-filter.ts`, wie `einzel()` serverseitig und
    // wie an `/lieferant/$name`.
    von:
      typeof roh.von === "string"
        ? roh.von
        : Array.isArray(roh.von) && typeof roh.von[0] === "string"
          ? roh.von[0]
          : "",
  }),
  search: { middlewares: [OHNE_LEERE_EINZELWERTE_UND_HERKUNFT] },
});

function ArtikelRoute() {
  const { id } = Route.useParams();
  /*
   * Der Filter kommt als PROP herein, nicht über einen Hook in der Sicht —
   * dieselbe Trennung, die diese Datei schon für `id` zieht, und aus
   * demselben Grund: ein `useSearch()` in `ArtikelSeite` zöge den Router in
   * jeden ihrer Tests, und zwar mit einer Route, die es dort nicht gibt.
   *
   * Die Rest-Destrukturierung ist dabei KEINE Kosmetik: sie ist der Grund,
   * warum `von` nicht in `alsSearch(filter)` landen kann und damit auch nicht
   * an den „Zurück zur Suche"-Link. Die Trennung von Filter und Herkunft ist
   * hier strukturell und nicht bloss eingehalten — wer `von` künftig doch in
   * den Filter zöge, müsste diese Zeile anfassen.
   */
  const { von, ...filter } = Route.useSearch();
  return <ArtikelSeite id={id} filter={filter} herkunft={herkunftAus(von)} />;
}
